import { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit, Trash2, X, Loader2 } from 'lucide-react';

export default function PublicacionesAdmin() {
  const [publicaciones, setPublicaciones] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pubSeleccionada, setPubSeleccionada] = useState(null);

  useEffect(() => { cargarPublicaciones(); }, []);

  const cargarPublicaciones = async () => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/publicaciones');
      const data = await res.json();
      if (data.success) setPublicaciones(data.data);
    } catch (error) { console.error("Error:", error); }
  };

  const abrirModal = (pub = null) => {
    setPubSeleccionada(pub);
    setIsModalOpen(true);
  };

  // Crear (POST) y Actualizar (PUT)
  const handleGuardar = async (formDataState) => {
    const url = pubSeleccionada 
      ? `https://logia-production-725b.up.railway.app/api/admin/publicaciones/${pubSeleccionada.id}`
      : 'https://logia-production-725b.up.railway.app/api/admin/publicaciones';
    const method = pubSeleccionada ? 'PUT' : 'POST';

    // Usar FormData para envío de archivos multipart
    const formData = new FormData();
    formData.append('titulo', formDataState.titulo);
    formData.append('descripcion_corta', formDataState.descripcion_corta);
    if (formDataState.pdf) formData.append('pdf', formDataState.pdf);
    if (formDataState.caratula) formData.append('caratula', formDataState.caratula);

    try {
      const res = await fetch(url, {
        method,
        body: formData // Fetch setea automáticamente el Content-Type adecuado con el Boundary
      });
      const data = await res.json();
      if (data.success) {
        setIsModalOpen(false);
        cargarPublicaciones();
      } else alert('Error al guardar: ' + data.error);
    } catch (error) { alert('Error de conexión.'); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta revista/documento?")) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/admin/publicaciones/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        cargarPublicaciones();
      } else alert('Error al eliminar: ' + data.error);
    } catch (error) { alert('Error de conexión.'); }
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in relative">
      <div className="flex justify-between items-center bg-[#0a0a0a] border border-white/10 p-6 shadow-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
        <div className="relative z-10">
          <h1 className="font-serif text-3xl md:text-4xl text-[#C5A059] m-0 tracking-wide flex items-center gap-3">
            <BookOpen size={32} /> Gestión de Publicaciones
          </h1>
          <p className="text-gray-400 font-light text-sm mt-2 m-0">Controla revistas en PDF y documentos oficiales.</p>
        </div>
        <button onClick={() => abrirModal()} className="relative z-10 flex items-center gap-2 px-6 py-3 text-xs tracking-widest uppercase font-bold bg-[#C5A059] text-[#050505] hover:bg-white transition-colors">
          <Plus size={16} /> Nueva Publicación
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {publicaciones.map((pub) => (
          <div key={pub.id} className="bg-[#050505] border border-white/5 p-6 shadow-lg hover:border-[#C5A059]/30 transition-colors flex flex-col justify-between">
            <div>
              {pub.caratula_url && (
                <div className="mb-4 w-full h-32 overflow-hidden flex justify-center items-center bg-black/50 border border-white/10">
                   <img src={pub.caratula_url} alt="Portada" className="h-full object-cover" />
                </div>
              )}
              <h4 className="text-white text-lg font-medium tracking-wide m-0 mb-2">{pub.titulo}</h4>
              <p className="text-gray-400 text-sm mb-4 line-clamp-3">{pub.descripcion_corta}</p>
            </div>
            <div className="flex justify-between items-center pt-4 border-t border-white/10">
              <a href={pub.pdf_url} target="_blank" rel="noreferrer" className="text-[#C5A059] text-[10px] tracking-widest uppercase hover:text-white transition-colors">Leer PDF</a>
              <div className="flex gap-3">
                <button onClick={() => abrirModal(pub)} className="text-gray-500 hover:text-[#C5A059] transition-colors"><Edit size={16} /></button>
                <button onClick={() => handleEliminar(pub.id)} className="text-gray-500 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && <PublicacionModal pubData={pubSeleccionada} onClose={() => setIsModalOpen(false)} onSave={handleGuardar} />}
    </div>
  );
}

function PublicacionModal({ pubData, onClose, onSave }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    titulo: pubData?.titulo || '',
    descripcion_corta: pubData?.descripcion_corta || '',
    pdf: null,
    caratula: null
  });

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    setIsLoading(true); 
    await onSave(formData); 
    setIsLoading(false); 
  };

  return (
    <div className="fixed inset-0 z-[999] flex justify-center items-start pb-10 px-4 sm:px-6 bg-[#050505]/90 backdrop-blur-sm">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 w-full max-w-lg shadow-2xl relative flex flex-col max-h-[calc(100vh-160px)] animate-fade-in">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50 shrink-0"></div>
        
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-white/10 shrink-0">
          <h2 className="font-serif text-xl md:text-2xl text-white m-0">{pubData ? 'Editar Publicación' : 'Nueva Publicación'}</h2>
          <button onClick={onClose} disabled={isLoading} className="bg-transparent border-none text-gray-500 hover:text-white cursor-pointer p-1"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-5 md:p-6 overflow-y-auto flex-1 flex flex-col gap-4 hide-scrollbar">
            
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Título</label>
              <input type="text" value={formData.titulo} onChange={e => setFormData({...formData, titulo: e.target.value})} required disabled={isLoading} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] disabled:opacity-50" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Descripción Corta</label>
              <textarea value={formData.descripcion_corta} onChange={e => setFormData({...formData, descripcion_corta: e.target.value})} disabled={isLoading} rows="3" className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] resize-none disabled:opacity-50"></textarea>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Archivo PDF {pubData && '(Opcional si no se cambia)'}</label>
              
              {/* VISTA PREVIA DEL PDF ACTUAL */}
              {pubData?.pdf_url && (
                <div className="flex items-center gap-3 mb-2 p-3 bg-black/30 border border-white/10 rounded">
                  <div className="flex flex-col">
                    <span className="text-xs text-green-400 font-semibold">✓ PDF actual guardado</span>
                    <a href={pubData.pdf_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#C5A059] hover:underline">Click aquí para ver el documento actual</a>
                  </div>
                </div>
              )}

              <input type="file" accept="application/pdf" onChange={e => setFormData({...formData, pdf: e.target.files[0]})} required={!pubData} disabled={isLoading} className="text-gray-400 text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:font-semibold file:bg-[#C5A059] file:text-[#050505] hover:file:bg-white disabled:opacity-50" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Carátula (Imagen Opcional)</label>
              
              {/* VISTA PREVIA DE CARATULA ACTUAL */}
              {pubData?.caratula_url && (
                <div className="flex items-center gap-4 mb-2 p-3 bg-black/30 border border-white/10 rounded">
                  <img src={pubData.caratula_url} alt="Carátula" className="w-12 h-16 object-cover rounded border border-white/20" />
                  <div className="flex flex-col">
                    <span className="text-xs text-green-400 font-semibold">✓ Carátula actual guardada</span>
                  </div>
                </div>
              )}

              <input type="file" accept="image/*" onChange={e => setFormData({...formData, caratula: e.target.files[0]})} disabled={isLoading} className="text-gray-400 text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:font-semibold file:bg-[#C5A059] file:text-[#050505] hover:file:bg-white disabled:opacity-50" />
            </div>

          </div>

          <div className="p-5 border-t border-white/10 flex justify-end gap-3 shrink-0 bg-[#050505]">
            <button type="button" onClick={onClose} disabled={isLoading} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors disabled:opacity-50">Cancelar</button>
            <button type="submit" disabled={isLoading} className={`flex items-center gap-2 bg-[#C5A059] hover:bg-white text-[#050505] px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors border-none ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}>
              {isLoading ? <><Loader2 size={16} className="animate-spin" /> Guardando...</> : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}